import { OrganigrammeLink } from './link';
import { OrganigrammeEntity, OrganigrammeEntityType } from './entity';

import {
  SVG_NS, GRID_CELL_HEIGHT, GRID_CELL_WIDTH, NODE_FONT_FAMILY, NODE_FONT_SIZE, NODE_DEFAULT_HEIGHT, NODE_RADIUS, NODE_DEFAULT_WIDTH, snapToGridX
} from '../organigramme.settings';


export class OrganigrammePoint {
  constructor(public x: number, public y: number) {}

  equals(point: OrganigrammePoint): boolean {
    if(!point) {
      return false;
    }

    return point.x === this.x && point.y === this.y;
  }

  snapToGrid() {
    this.x = snapToGridX(this.x);
    this.y = snapToGridX(this.y);
  }
}

export enum NodeSide {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT'
}


export class OrganigrammeNode {
  links: OrganigrammeLink[] = []
  
  x: number
  y: number
  height = NODE_DEFAULT_HEIGHT
  width: number = NODE_DEFAULT_WIDTH
  rx = 0
  ry = 0

  private _id: string

  constructor(public entity: OrganigrammeEntity) {
    // Initialize layout
    this.initLayout();
  }

  private initLayout() {
    // Calculate text width
    let $svg = document.createElementNS(SVG_NS, 'svg');
    $svg.setAttribute('class', 'organigramme');
    
    // Hide the svg element
    $svg.style.position = 'fixed';
    $svg.style.top = '-1000px';
    
    let $g = document.createElementNS(SVG_NS, 'g');
    $g.setAttribute('class', 'node-' + this.type);

    let $text = document.createElementNS(SVG_NS ,'text');
    $text.style.fontFamily = NODE_FONT_FAMILY;
    $text.style.fontSize = NODE_FONT_SIZE;
    $text.appendChild(document.createTextNode(this.name));

    // Set rounded rect
    if(this.type === 'dossier') {
      this.rx = this.ry = NODE_RADIUS;
    }
  }


  get id() {
    return this.entity.id;
  }

  
  get type(): OrganigrammeEntityType {
    return this.entity.type;
  }


  get name() {
    return this.entity.name;
  }

  get dossier_type() {
    return this.entity.dossier_type;
  }


  get topAnchorPoint() {
    return new OrganigrammePoint(this.x + (this.width / 2), this.y);
  }

  get bottomAnchorPoint() {
    return new OrganigrammePoint(this.x + (this.width / 2), this.y + this.height);
  }

  get leftAnchorPoint() {
    return new OrganigrammePoint(this.x, this.y + (this.height / 2));
  }

  get rightAnchorPoint() {
    return new OrganigrammePoint(this.x + this.width, this.y + (this.height / 2));
  }


  get center() {
    return new OrganigrammePoint(this.x + (this.width / 2), this.y + (this.height / 2));
  }

  getSide(target: OrganigrammePoint) : NodeSide {
    let {x: centerX, y: centerY} = this.center;
    let {x, y} = target;

    // Transform coordinates to the center
    x = x - centerX;
    y = -(y - centerY); // Y axis should be reversed

    // Calculate degree compared to the center
    let deg = Math.atan2(y, x);

    if(deg < 0) {
      deg = (Math.PI * 2) + deg;
    }

    // Calculate top right corner degree
    let topRightX = (this.x + this.width) - centerX;
    let topRightY = -(this.y - centerY);

    const DEG_TR = Math.atan2(topRightY, topRightX);
    const DEG_TL = Math.PI - DEG_TR;
    const DEG_BL = Math.PI + DEG_TR;
    const DEG_BR = (2 * Math.PI) - DEG_TR;

    
    // Detect the side
    if((deg >= 0 && deg <= DEG_TR) || (deg <= (2* Math.PI) && deg > DEG_BR)) {
      return NodeSide.RIGHT;
    }
    else if(deg > DEG_TR && deg <= DEG_TL) {
      return NodeSide.TOP;
    }
    else if(deg > DEG_TL && deg <= DEG_BL) {
      return NodeSide.LEFT;
    }
    else if(deg > DEG_BL && deg <= DEG_BR) {
      return NodeSide.BOTTOM;
    }
  }


  getAnchorForSide(side: NodeSide) {
    if(side === NodeSide.TOP) {
      return this.topAnchorPoint;
    }
    else if(side === NodeSide.BOTTOM) {
      return this.bottomAnchorPoint;
    }
    else if(side === NodeSide.LEFT) {
      return this.leftAnchorPoint;
    }
    else if(side === NodeSide.RIGHT) {
      return this.rightAnchorPoint;
    }
  }

  // Export
  exportData() {
    return {
      nodeId: this.id,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      name: this.name,
      type: this.type,
      dossier_type: this.dossier_type
    };
  }
}